import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { styles } from '@/modules/agreements/platform/pages/document-create-page/styles';
import {
  requiredActionLabel,
  verificationPolicyLabel,
} from '@/modules/agreements/shared';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';

/**
 * Step 4 — the read-back before anything is written.
 *
 * This step exists because the three writes are NOT atomic and there is no
 * rollback. Publishing is the point of no return for the version's content: a
 * version is immutable once published, so a typo in the text is not something
 * the operator can fix afterwards — they would have to publish a new version.
 * Showing the exact text before the call is the last chance to catch it.
 *
 * The content is rendered as PLAIN TEXT, not as markup. It is the literal string
 * that will be stored and shown to a subject, and rendering it as HTML here
 * would show the operator something other than what they are about to publish.
 */
export function ReviewStep(props: { draft: DocumentDraft }): ReactNode {
  const { draft } = props;
  const audience = draft.audience === 'both' ? 'tenant' : draft.audience;

  return (
    <div className="space-y-4">
      <dl className={styles.reviewGrid}>
        <ReviewField label="Document key" value={draft.documentKey} mono />
        <ReviewField label="Title" value={draft.title.trim()} />
        <ReviewField label="Audience" value={audience} />
        <ReviewField label="Document action" value={requiredActionLabel(draft.requiredAction)} />
        <ReviewField label="Locale" value={draft.locale} />
        <ReviewField label="Version title" value={draft.versionTitle.trim()} />
        <ReviewField label="Effective from" value={draft.effectiveAt} />
        <ReviewField
          label="Requirement action"
          value={requiredActionLabel(draft.requirementAction)}
        />
        <ReviewField
          label="Verification policy"
          value={
            draft.verificationPolicy
              ? verificationPolicyLabel(draft.verificationPolicy)
              : 'Server default (session re-attestation)'
          }
        />
        <ReviewField
          label="Re-acceptance on new version"
          value={draft.reacceptanceRequired ? 'Required' : 'Not required'}
        />
        <ReviewField label="Deadline" value={draft.deadlineAt || 'No deadline'} />
      </dl>

      <div className="space-y-2">
        <p className={styles.reviewTerm}>Content — exactly as the subject will read it</p>
        <div className={styles.preview}>{draft.content}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="info">Creates 1 document</Badge>
        <Badge tone="info">Publishes 1 version</Badge>
        <Badge tone="info">Creates 1 requirement</Badge>
      </div>

      <p className={styles.partialNoticeBody}>
        Publishing writes three things in order: the document, its first version, then the
        requirement. A version is immutable once published, so the content above cannot be
        edited afterwards — a correction means publishing a new version.
      </p>
    </div>
  );
}

/** One read-back row. `mono` for values that are identifiers rather than prose. */
function ReviewField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): ReactNode {
  return (
    <div>
      <dt className={styles.reviewTerm}>{label}</dt>
      <dd className={mono ? `${styles.reviewValue} font-mono` : styles.reviewValue}>{value}</dd>
    </div>
  );
}
